/**
 * Этап 5 / KS-02 — pure model behind the addressee picker.
 *
 * The picker has three pieces of real logic, and all three are here rather than inside
 * the component so they can be tested without a DOM:
 *   · which client's contact list to show;
 *   · what the current selection is, and whether it differs from what is saved;
 *   · how a contact is labelled when it has no email, or no name worth showing.
 */
import type { ContactDTO, ProjectContactsResponse } from '@eer/shared';

/** The selection the picker edits. */
export interface ContactSelection {
  /** Null = the project has no main addressee (a legitimate state). */
  readonly primaryId: number | null;
  readonly ccIds: readonly number[];
}

export const EMPTY_SELECTION: ContactSelection = { primaryId: null, ccIds: [] };

/** Read the saved selection out of the API response. */
export function selectionFrom(saved: ProjectContactsResponse | undefined): ContactSelection {
  if (!saved) return EMPTY_SELECTION;
  return {
    primaryId: saved.primary?.id ?? null,
    ccIds: saved.cc.map((c) => c.id),
  };
}

/** Order-insensitive comparison — the CC list is a SET, and the UI may reorder it. */
export function sameSelection(a: ContactSelection, b: ContactSelection): boolean {
  if (a.primaryId !== b.primaryId) return false;
  if (a.ccIds.length !== b.ccIds.length) return false;
  const bs = new Set(b.ccIds);
  return a.ccIds.every((id) => bs.has(id));
}

/**
 * Make `id` the primary.
 *
 * It is also REMOVED from CC: a contact cannot be both the To: and a copy, and the
 * server would drop it anyway. Doing it here keeps the checkbox from staying visibly
 * ticked after the radio moves — a state the user would read as "in both".
 */
export function withPrimary(sel: ContactSelection, id: number | null): ContactSelection {
  return { primaryId: id, ccIds: sel.ccIds.filter((cc) => cc !== id) };
}

/**
 * E02-F08-T07 — make a freshly created contact the primary WITHOUT dropping the old one.
 *
 * "Add and select" is a different gesture from picking the radio: the user is adding a
 * person who was missing, not replacing the one already addressed. Plain {@link withPrimary}
 * silently unlinked the previous primary (prod, project 148: Ashley Topaz vanished and had
 * to be re-added by hand). The previous primary is moved into CC instead — still on the
 * project, still visible, and one click away from being unticked if that was the intent.
 */
export function withAddedPrimary(sel: ContactSelection, id: number): ContactSelection {
  const prev = sel.primaryId;
  const ccIds = sel.ccIds.filter((cc) => cc !== id);
  if (prev !== null && prev !== id && !ccIds.includes(prev)) ccIds.push(prev);
  return { primaryId: id, ccIds };
}

/** Toggle `id` in CC. Ticking the current primary MOVES it out of primary. */
export function withCcToggled(sel: ContactSelection, id: number): ContactSelection {
  if (sel.ccIds.includes(id)) {
    return { ...sel, ccIds: sel.ccIds.filter((cc) => cc !== id) };
  }
  const primaryId = sel.primaryId === id ? null : sel.primaryId;
  return { primaryId, ccIds: [...sel.ccIds, id] };
}

/**
 * The client whose contacts the picker lists.
 *
 * The project's own client wins; a contact's own `client_id` is the fallback for
 * legacy rows where the project has no client yet (same precedence as the detail
 * card's `resolveClientId` — ADR PK-QA-9). Without the fallback, a project created
 * from an email with an unresolved client would show an empty picker even though its
 * contact plainly belongs to a client.
 */
export function pickerClientId(
  projectClientId: number | null | undefined,
  saved: ProjectContactsResponse | undefined,
): number | null {
  return projectClientId ?? saved?.primary?.client_id ?? null;
}

/**
 * How one contact reads in the list.
 *
 * A contact with no email is shown with a hint rather than hidden: it is a real
 * directory row a human may still want as the addressee (they will add the address),
 * and silently omitting it reproduces "the contact I know exists is not offered".
 */
export function contactLabel(c: ContactDTO): string {
  const name = c.name.trim() || 'Unnamed';
  const email = (c.email ?? '').trim();
  const position = (c.position ?? '').trim();
  const head = position ? `${name}, ${position}` : name;
  return email ? `${head} — ${email}` : `${head} — no email`;
}

/** True when this contact cannot receive an email (drives the CC-disabled hint). */
export function lacksEmail(c: ContactDTO): boolean {
  return (c.email ?? '').trim().length === 0;
}

/**
 * The contacts to render, with the saved ones guaranteed present.
 *
 * A saved addressee whose client was changed (or who was moved to another client in
 * the directory) is no longer in the client's list, but it IS still an addressee of
 * this project. Dropping it from the render would show the project as addressed to
 * fewer people than it is — so it is appended instead, and the caller marks it.
 */
export function visibleContacts(
  clientContacts: readonly ContactDTO[],
  saved: ProjectContactsResponse | undefined,
): readonly ContactDTO[] {
  const known = new Set(clientContacts.map((c) => c.id));
  const extras: ContactDTO[] = [];
  for (const c of [...(saved?.primary ? [saved.primary] : []), ...(saved?.cc ?? [])]) {
    if (!known.has(c.id)) {
      known.add(c.id);
      extras.push(c);
    }
  }
  return [...clientContacts, ...extras];
}
